import { TableMapping } from "@made-people/centra-checkout-api";
import { TranslateFunction } from "@made-people/grease-translations";
import { ProductVariant } from "@made-people/grease-product";
import {
  isWeeksStatus,
  parseLitWarehouseStatus,
} from "../../../services/centra/mappers/utils/parse-lit-warehouse-status";

const returnVirtualStock = (translate: TranslateFunction) => {
  return {
    deliveryStatus: translate("Delivery_Virtual_Stock"),
    isImmediateDelivery: true,
  };
};

const returnInStock = (translate: TranslateFunction) => {
  return {
    deliveryStatus: translate("Delivery_In_Stock"),
    isImmediateDelivery: true,
  };
};

const returnIn2to4Weeks = (translate: TranslateFunction) => {
  return {
    deliveryStatus: translate("Delivery_In_2_4_Weeks"),
    isImmediateDelivery: false,
  };
};

const returnOutOfStock = (translate: TranslateFunction) => {
  return {
    deliveryStatus: translate("Delivery_Out_Of_Stock"),
    isImmediateDelivery: false,
  };
};

/**
 * Get the variant stock status by looking at the LIT warehouse status and the variant inventory, used for PDP and PLP delivery status
 * @param variant - The variant object
 * @param productBase - The product base object
 * @param sizeLabel - The size label
 * @param translate
 * @returns The delivery status and is immediate delivery
 */
export function getVariantStockStatus(
  variant: ProductVariant,
  productBase: {
    discontinued: boolean;
    litWarehouseStatus: string | null;
    tableMapping: TableMapping;
  },
  sizeLabel: string,
  translate: TranslateFunction,
): {
  deliveryStatus: string;
  isImmediateDelivery: boolean;
} {
  const litStatus = parseLitWarehouseStatus(
    productBase.litWarehouseStatus,
    sizeLabel,
    productBase.tableMapping,
  );

  const hasStock = variant.inventory.quantity > 0;

  if (productBase.litWarehouseStatus && litStatus === null) {
    console.warn(
      `[getVariantStockStatus]: Could not find size '${sizeLabel}' in LIT warehouse status JSON. Available keys:`,
      productBase.litWarehouseStatus
        ? Object.keys(JSON.parse(productBase.litWarehouseStatus))
        : [],
    );
  }

  if (hasStock) {
    if (litStatus === "OMG") {
      return returnInStock(translate);
    }

    return returnVirtualStock(translate);
  }

  if (isWeeksStatus(litStatus)) {
    return returnIn2to4Weeks(translate);
  }

  return returnOutOfStock(translate);
}

/**
 * Get the variant stock status by only looking at the LIT warehouse status, used for line item delivery status
 * @param productBase - The product base object
 * @param sizeLabel - The size label
 * @param translate - The translate function
 * @returns The delivery status and is immediate delivery
 */
export function getVariantStockStatusByLitOnly(
  productBase: {
    litWarehouseStatus: string | null;
    tableMapping: TableMapping;
  },
  sizeLabel: string,
  translate: TranslateFunction,
): {
  deliveryStatus: string;
  isImmediateDelivery: boolean;
} {
  const litStatus = parseLitWarehouseStatus(
    productBase.litWarehouseStatus,
    sizeLabel,
    productBase.tableMapping,
  );

  if (productBase.litWarehouseStatus && litStatus === null) {
    console.warn(
      `[getVariantStockStatusByLitOnly]: Could not find size '${sizeLabel}' in LIT warehouse status JSON. Available keys:`,
      productBase.litWarehouseStatus
        ? Object.keys(JSON.parse(productBase.litWarehouseStatus))
        : [],
    );
  }

  if (litStatus === "OMG") {
    return returnInStock(translate);
  }

  if (isWeeksStatus(litStatus)) {
    return returnIn2to4Weeks(translate);
  }

  if (litStatus === "-") {
    return returnVirtualStock(translate);
  }

  return returnOutOfStock(translate);
}
